import uniqBy from 'lodash/uniqBy'
import i18next from 'i18next'
import last from 'lodash/last'
import { useState, useEffect } from 'react'
import { useLocation } from 'react-router'

import { Link } from 'features/i18n/router'
import { GroupMember } from 'lib/_types'
import { logEvent } from 'lib/analytics'
import { ROUTE_PATH } from 'lib/paths'
import { useInfinityScrollSimple } from 'hooks'
import {
  useGetMembersQuery,
  useMemberRequestActionMutation,
} from 'store/api/yay/group'

import Loading from 'components/Loading'
import Avatar from '../Avatar'
import Nickname from '../Nickname'
import LoadMore from '../LoadMore'

const RequestJoinGroupModal = ({ groupId }: { groupId: string }) => {
  const location = useLocation()

  const [__fromId, __fromIdSet] = useState<number>(null)
  const [__groupMembers, __groupMembersSet] = useState<Array<GroupMember>>([])

  const [memberRequestAction] = useMemberRequestActionMutation()

  const groupMembersRes = useGetMembersQuery({
    groupId,
    fromId: __fromId,
    mode: 'pending',
  })

  const loadMore = () => {
    __fromIdSet(last(groupMembersRes?.data)?.user?.id)
  }

  const { loadMoreRef } = useInfinityScrollSimple({
    loadMore,
  })

  useEffect(() => {
    __groupMembersSet(
      uniqBy(
        [
          ...__groupMembers?.map?.((x) => {
            return (
              groupMembersRes?.currentData?.find?.(
                (y) => y?.user?.id == x?.user?.id
              ) ?? x
            )
          }),
          ...(groupMembersRes?.currentData ?? []),
        ],
        (x) => x?.user?.id
      )
    )
  }, [groupMembersRes])

  return (
    <div className="Modal__wrapper">
      <div className="Modal__header">
        <Link className="Modal__header__close" to={location.pathname} />

        <h2 className="Modal__header__h2">
          {i18next.t('request_join_group_modal.title')}
        </h2>
      </div>

      <div className="Modal__body">
        {groupMembersRes?.isFetching && (__groupMembers?.length ?? 0) === 0 && (
          <Loading />
        )}

        {__groupMembers?.length > 0 && (
          <dl className="ConfirmBox__users ConfirmBox__users--full">
            {__groupMembers?.map?.((x) => (
              <div
                className="ConfirmBox__users__item ConfirmBox__users__item--wide"
                key={x?.user?.id}
              >
                <Link
                  onClick={() =>
                    logEvent.user_profile_open({
                      target_user_id: x?.user?.id.toString(),
                      from: 'undefined',
                    })
                  }
                  to={ROUTE_PATH.USER(x?.user?.id)}
                >
                  <Avatar user={x?.user} showVipCircle showOnlineStatus />
                </Link>

                <dt>
                  <Link
                    onClick={() =>
                      logEvent.user_profile_open({
                        target_user_id: x?.user?.id.toString(),
                        from: 'undefined',
                      })
                    }
                    to={ROUTE_PATH.USER(x?.user?.id)}
                  >
                    <Nickname user={x?.user} />
                  </Link>
                </dt>

                <div className="ConfirmBox__users__item__buttons">
                  <a
                    className="Button Button--bordered-green Button--icon-check-green Button--group-join-request"
                    onClick={() => {
                      memberRequestAction({
                        groupId,
                        userId: x?.user?.id,
                        mode: 'accept',
                      }).then((res) => {
                        if (res?.['data']?.result == 'success') {
                          __groupMembersSet(
                            __groupMembers?.filter?.((y) => {
                              return y?.user?.id != x?.user?.id
                            })
                          )
                        }
                      })
                    }}
                  />

                  <a
                    className="Button Button--bordered-red Button--icon-x Button--group-join-request"
                    onClick={() => {
                      memberRequestAction({
                        groupId,
                        userId: x?.user?.id,
                        mode: 'decline',
                      }).then((res) => {
                        if (res?.['data']?.result == 'success') {
                          __groupMembersSet(
                            __groupMembers?.filter?.((y) => {
                              return y?.user?.id != x?.user?.id
                            })
                          )
                        }
                      })
                    }}
                  />
                </div>
              </div>
            ))}

            <LoadMore
              className="ConfirmBox__users__item ConfirmBox__users__item--wide ConfirmBox__users__item--load-more ConfirmBox__users__item--no-bdb"
              onClick={() => {
                loadMore()
              }}
              isLoading={groupMembersRes?.isFetching}
              hidden={groupMembersRes?.data?.length == 0}
              ref={loadMoreRef}
            />
          </dl>
        )}
      </div>
    </div>
  )
}

export default RequestJoinGroupModal
